import {
    AR_SSD,
    AR_V2, CORE_AMZL,
    DATE_TIME_FORMAT,
    DEFAULT_REFRESH_INTERVAL,
    ENVIRONMENT, FEATURES,
    REGION, REVERSE_PICK,
    ROUTING_ALGORITHM,
    SORT_DIRECTION,
    STAGE
} from "./Constants";
import {
    ASSIGNABLE, ASSIGNED,
    DEPART_IN_PROGRESS,
    HIGH_RISK_STATUS, IN_PROGRESS, NOT_READY, NOT_STARTED,
    PARTIALLY_DEPARTED, PARTIALLY_STAGED, PROGRESSING,
    ROUTE_CUT,
    SIDELINED, STAGED
} from "./EntityStatusManager";
import {getStationCode} from "../network/NetworkUtil";
import {getDocumentURL, getFeaturesFromParent} from "../handler/ParentDataHandler";

export function isDevelopmentEnv(){
    return process.env.NODE_ENV !== ENVIRONMENT.PROD && process.env.NODE_ENV !== ENVIRONMENT.TEST;
}

export function calculatePickDurationWithMultipleAssociates(route) {
    if (route.pickAssociates.length > 0) {
        let startTimes = route.pickAssociates.map(associate => associate.startTime);
        let endTimes = route.pickAssociates
            .map(associate => getAssignmentEndTime(associate))
            .filter(e => e);
        let minStartTime = Math.min(...startTimes);
        if (Array.isArray(endTimes) && endTimes.length > 0) {
            let maxEndTime = Math.max(...endTimes);
            return convertMillisToSecond(maxEndTime - minStartTime);
        } else {
            return getTimeDiffFromCurrentTimeInHHMMSSFormat(minStartTime);
        }
    }
    return null;
}

export function isRouteAtRisk(route, sprPackageCountThreshold, sprTimeLimitThreshold) {
    const maxDiffTimeAllowed = sprTimeLimitThreshold * 60000; // convert into milliseconds
    const timeDiff = route.criticalPullTime - new Date().getTime();
    const isTimeCrunch = timeDiff < maxDiffTimeAllowed;
    const isHighSPR = route.routeSPR >= sprPackageCountThreshold;
    const isRouteStatusRisky = HIGH_RISK_STATUS.filter(status => route.routeStatus.includes(status)).length > 0;
    return isTimeCrunch && isHighSPR && isRouteStatusRisky;
}

export function calculatePickDurationWithSingleAssociate(route) {
    if (route.pickAssociates.length > 0) {
        if (route.pickAssociates[0].pickingDuration != null) {
            return convertMillisToSecond(route.pickAssociates[0].pickingDuration);
        } else if (route.pickAssociates[0].startTime != null) {
            return getTimeDiffFromCurrentTimeInHHMMSSFormat(route.pickAssociates[0].startTime)
        }
    }
    return null;
}

export function isARorSSD(capability) {
    return capability && AR_SSD.includes(capability);
}

export function isDynamicRoute(algorithm) {
    return algorithm && ROUTING_ALGORITHM.DYNAMIC === algorithm;
}

export function isReversePicklist(route) {
    const picklists = route && route.picklists;

    if (picklists && picklists.length > 0) {
        const picklist = picklists[0];

        const workGroupSequenceList = picklist && picklist.workGroupSequenceList;

        if (workGroupSequenceList && workGroupSequenceList.length > 0) {
            const type = workGroupSequenceList[0].type && workGroupSequenceList[0].type.toString();

            return type === REVERSE_PICK;
        }
    }

    return false;
}

export function isPicklistSplittingComplete(route) {
    const picklists = route && route.picklists;

    if (picklists && route.picklists.length > 0) {
        const picklist = route.picklists[0];

        const workGroupSequenceList = picklist && picklist.workGroupSequenceList;

        if (!workGroupSequenceList || picklist.workGroupSequenceList.length <= 0) {
            ///Workgroupsequence is added upon splitting
            return false;
        }
    }
    return true;
}

export function isAMZLorARv2(capability) {
    return capability && (capability === CORE_AMZL || capability === AR_V2);
}

export function getTimeDiffFromCurrentTimeInHHMMSSFormat(time) {
    if (!time) {
        return null;
    }
    const curDate = new Date();
    const timeDiff = curDate.getTime() - time;
    return convertMillisToSecond(timeDiff);
}

export const nullIfDefault = (value) => {
    return !value || value === "..." ? "NA" : value;
}

export function convertMillisToSecond(time) {
    return Math.ceil(time/1000)
}

export function getAssignmentEndTime(associate) {
    if (isTimeValid(associate.startTime) && !isTimeValid(associate.endTime)) {
        return new Date().getTime()
    } else {
        return associate.endTime;
    }
}

export function isTimeValid(time) {
    return time !== null & time !== 0;
}

export function convertSecondsToTimeFormat(seconds) {
    const hours = Math.floor(seconds / 3600) % 24;
    const minutes = Math.floor(seconds / 60) % 60;
    const secs = Math.round(seconds % 60);
    return ([hours, minutes, secs]
        .map(v => v < 10 ? "0" + v : v)
        .join(":"));
}

export function convertSecondsToMinutesFormat(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return ([minutes, secs]
        .map(v => v < 10 ? "0" + v : v)
        .join(":"));
}

export function convertSecondsToHHMM(milliseconds) {
    const time = new Date(parseInt(milliseconds, 10));
    return ('0' + time.getHours()).slice(-2) + ':' + ('0' + time.getMinutes()).slice(-2);
}

/**
 * Input is epoch, output is date-time in browser local time zone
 * For eg:
 *  Input: 1637145000000
 *  Output: 2021/11/17 16:00:00 (Sweden date time format)
 * Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleString
 */
export function convertEpochToLocaleFormat(epoch) {
    if (epoch && typeof epoch === 'number') {
        return new Date(epoch).toLocaleString('sv')
    }
    return "";
}

export function getDateInMMDDYYYYWithOffset(offset){
    let presentDate = new Date();
    presentDate.setDate(presentDate.getDate()+offset)
    return (presentDate.getMonth() + 1) + "/" + presentDate.getDate() + "/" + presentDate.getFullYear();
}

export function getDateInYYYYMMDDWithOffset(offset){
    let presentDate = new Date();
    presentDate.setDate(presentDate.getDate()+offset)
    return presentDate.toISOString().split('T')[0];
}

export function convertEpochMillisToLocalTime(epochTime) {
    const options = {hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false};
    const date = new Date(epochTime);
    return date.toLocaleString('en-GB', options);
}

export function convertEpochMillisToLocalDateTime(epochTime) {
    const options = {month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false};
    const date = new Date(epochTime);
    return date.toLocaleString('en-GB', options);
}

export function getBaseUrl(endpoint) {
    const url = new URL(endpoint);
    return url.origin
}

/**
 * Return phone tool url for associate alias.
 * @param alias
 * @return {string}
 */
export function getPhoneToolLink(alias) {
    return "https://phonetool.amazon.com/users/" + formatAlias(alias);
}

/**
 * Get DPO URL based on region and stage and station code.
 * @returns {string}
 */
export function getDispatchPlanningURL() {
    return `https://${getDispatchPlanningRegionalSuffix()}.dispatch.${getStage() !== STAGE.PROD ? "gamma." : ""}` +
        `planning.last-mile.a2z.com/dispatch-planning/${getStationCode()}`
}

function getDispatchPlanningRegionalSuffix() {
    switch (getRegion()) {
        case REGION.EU: return "eu";
        case REGION.NA: return "na";
        default: return "jp";
    }
}

/**
 *  Checks if value is not equal to "NA", then it decodes the value.
 * @param value
 * @return {string}
 */
export function parseURLParam(value) {
    if (value && value !== "NA") {
        return decodeURIComponent(value);
    }
    return "...";
}

export const chainWalk = (func, defaultValue) => {
    try {
        const value = func()
        return (value === null || value === undefined) ? defaultValue : value;
    } catch (e) {
        return defaultValue
    }
}

/**
 * This method return the refresh interval in milliseconds
 * @returns {number}
 */
export function getRefreshInterval(stationConfig) {
    if (stationConfig) {
        return stationConfig.refreshInterval;
    }
    return DEFAULT_REFRESH_INTERVAL;
}

/**
 * Merge Pick Data with Route data
 * @param routePackageMetrics
 * @param routeList
 * @returns {*}
 */
export function getPickedData(routePackageMetrics, state) {
    state.routeList.forEach(route => {
        const data = routePackageMetrics.find(pickData => {
            return pickData.routeId === route.routeId;
        });
        if (data) {
            route.picked = data.picked;
            route.staged = data.staged;
            route.departed = data.departed;
            route.problemSolve = data.problemSolve;
        }
    })
    return state;
}

export function allRouteMetricAvailable(newState) {
    return newState.metricAvailability.pick
        && newState.metricAvailability.stage
        && newState.metricAvailability.depart
        && newState.metricAvailability.stow
        && newState.metricAvailability.induct
}

export function computeRouteCounts(newState, capability) {
    newState.routeList.forEach(route => {
        //edge case all counts 0
        if (route.inducted === 0 && route.stowed === 0 && route.picked === 0 && route.staged === 0
            && route.departed === 0 && route.problemSolve === 0) {
            if (route.planned > 0) {
                newState.routeState.inducted += 1;
            }
            return;
        }

        if (capability === AR_V2) {
            if (route.inducted === 0) {
                newState.routeState.picked += 1;
            }
            if (route.inducted === 0 && route.picked === 0) {
                newState.routeState.stowed += 1;
            }
        } else {
            if (route.planned > 0 && (route.inducted === 0 || route.inducted === route.planned)) {
                newState.routeState.inducted += 1;
            }
            if (route.inducted === 0) {
                newState.routeState.stowed += 1;
            }
            if (route.inducted === 0 && route.stowed === 0) {
                newState.routeState.picked += 1;
            }
        }
        if (route.inducted === 0 && route.stowed === 0 && route.picked === 0) {
            newState.routeState.staged += 1;
        }
        if (route.inducted === 0 && route.stowed === 0 && route.picked === 0 && route.staged === 0 && route.departed > 0) {
            newState.routeState.departed += 1;
        }
    });
    return newState;
}

/**
 * Merge Induct Data with Route data
 * @param routePackageMetrics
 * @param routeList
 * @returns {*}
 */
export function getInductedData(routePackageMetrics, state, capability) {
    state.routeList.forEach(route => {
        const data = routePackageMetrics.find(inductData => {
            return inductData.routeId === route.routeId;
        });
        if (data) {
            route.inducted = data.inducted;
        }
    })
    return state;
}

/**
 * Merge Stow Data with Route data
 * @param routePackageMetrics
 * @param routeList
 * @returns {*}
 */
export function getStowedData(routePackageMetrics, state, capability) {
    state.routeList.forEach(route => {
        const data = routePackageMetrics.find(inductData => {
            return inductData.routeId === route.routeId;
        });
        if (data) {
            route.stowed = data.stowed;
        }
    })
    return state;
}

export function getTimeInSecond(time) {
    if (!time) {
        return 0;
    }
    const splitTime = time.split(":");
    if (splitTime.length > 2) {
        return (parseInt(splitTime[0], 10) * 60 * 60) + (parseInt(splitTime[1], 10) * 60)
            + parseInt(splitTime[2], 10);
    } else {
        return (parseInt(splitTime[0], 10) * 60) + parseInt(splitTime[1], 10);
    }
}

export function parseCapabilitiesResponse(response) {
    return response ? JSON.parse(response.capability) : '';
}

export function formatAlias(alias) {
    return (alias != null && alias.indexOf('@') > -1 ? alias.split('@')[0] : alias);
}

/**
 * Return average picking duration and its unit (mins|hrs) at route granularity
 * @param routes
 * @param stationConfig
 * @return {{unit: (string), timeToShow: string}}
 */
export function getAveragePickTime(routes, stationConfig) {
    let avgTimeInSeconds;
    if (stationConfig.parallelPickEnabled) {
        avgTimeInSeconds = calculateAveragePickEffortTimeOfRoutesInSeconds(routes);
    } else {
        avgTimeInSeconds = calculateAveragePickDurationInSeconds(routes);
    }

    return convertSecondsToMetricUnitPair(avgTimeInSeconds);
}

function convertSecondsToMetricUnitPair(timeInSeconds){
    let timeToShow = "";
    if (timeInSeconds > 0) {
        timeToShow = convertSecondsToMinutesFormat(timeInSeconds);
    }
    const unit = timeToShow && timeToShow.match(/:/gi).length > 1 ? "hrs" : timeToShow ? "mins" : "-";
    return {
        timeToShow,
        unit
    }
}

/**
 * Return average picking duration and its unit (mins|hrs) at picklist granularity
 * @param routes
 * @param stationConfig
 * @return {{unit: (string), timeToShow: string}}
 */
export function getAveragePickDurationOfPicklists(routes, stationConfig) {
    if (stationConfig.parallelPickEnabled) {
        let avgTimeInSeconds = calculateAveragePickEffortTimeOfPicklistsInSeconds(routes);
        return convertSecondsToMetricUnitPair(avgTimeInSeconds);
    }
    return {
        timeToShow: "",
        unit: "-"
    };
}

/**
 * Calculate route total duration ( pickDurationInSeconds = max end time - min start time).
 * @param routes
 * @return {number|number}
 */
export function calculateAveragePickDurationInSeconds(routes) {
    let totalTime = 0;
    let totalValidPickedRoutes = 0;
    for (let i = 0; i < routes.length; i++) {
        if (routes[i].pickDurationInSeconds) {
            totalTime = totalTime + routes[i].pickDurationInSeconds;
            totalValidPickedRoutes++;
        }
    }
    return (totalTime > 0) ? Math.round(totalTime / totalValidPickedRoutes) : 0;
}

/**
 * Calculate route pick effort time ( Average duration of all underlying picklists)
 * @param routes
 * @return {number|number}
 */
export function calculateAveragePickEffortTimeOfRoutesInSeconds(routes) {
    let totalTime = 0;
    let totalValidPickedRoutes = 0;
    for (let i = 0; i < routes.length; i++) {
        if (Array.isArray(routes[i].picklists) && routes[i].picklists.length) {
            let routeDuration = routes[i].picklists
                .map(picklist => picklist.pickDurationInSeconds || 0)
                .reduce((a, b) => a + b, 0);
            if (routeDuration) {
                totalTime = totalTime + routeDuration;
                totalValidPickedRoutes++;
            }
        }
    }

    return (totalTime > 0) ? Math.round(totalTime / totalValidPickedRoutes) : 0;
}

/**
 * Calculate picklists pick effort time ( Average duration of all picklists)
 * @param routes
 * @return {number|number}
 */
export function calculateAveragePickEffortTimeOfPicklistsInSeconds(routes) {
    let totalTime = 0;
    let totalValidPicklists = 0;
    for (let i = 0; i < routes.length; i++) {
        if (Array.isArray(routes[i].picklists) && routes[i].picklists.length) {
            for (let j = 0; j < routes[i].picklists.length; j++) {
                if (routes[i].picklists[j].pickDurationInSeconds) {
                    totalTime += routes[i].picklists[j].pickDurationInSeconds;
                    totalValidPicklists++;
                }
            }
        }
    }
    return (totalTime > 0) ? Math.round(totalTime / totalValidPicklists) : 0;
}

/**
 * Compare 2 arrays of string and returns true if they are equal.
 * @param array1
 * @param array2
 * @return {boolean}
 */
export function isArrayEquals(array1, array2) {
    if (!array1 || !array2 || array1.length !== array2.length) {
        return false;
    }
    const sortedArray1 = array1.sort();
    const sortedArray2 = array2.sort();
    for(let i = sortedArray1.length; i--;) {
        if(sortedArray1[i] !== sortedArray2[i])
            return false;
    }
    return true;
}

/**
 * Handles exception while reading from localStorage
 * @param key
 */
export function getFromLocalStorage(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

/**
 * Handles exception while writing into localStorage
 * @param key
 * @param value
 */
export function saveIntoLocalStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
    }
}

/**
 * Return cluster from route code.
 * Supports routing 3.0 regex.
 *
 * @param routeCode
 * @return {*}
 */
export function getCluster(routeCode) {
    if (!routeCode) {
        return "";
    }
    return routeCode.split("-")[0].replace(/[0-9]/g, '');
}

/**
 * Sorts input list of objects in ascending order by sequence number attribute.
 */
export function sortObjectsBySequenceNumber(list) {
    if (!Array.isArray(list)) {
        return list;
    }

    return list.sort((a, b) => {
        /**
         * Return -ve if B's sequence number is bigger than A : no change
         * Return +ve if A's sequence number is bigger than B : Should be swapped
         * Return zero if both are same : no change
         */
        if (a.sequenceNumber && b.sequenceNumber) {
            return a.sequenceNumber - b.sequenceNumber
        } else if (!a.sequenceNumber) {
            return 1;   //push A down in list by returning +ve number
        } else if (!b.sequenceNumber){
            return -1;  //keep A higher in list by returning -ve number
        }
        return 0;
    })
}

/**
 * Saves filter state into local storage along with current station code.
 * It also translates the newState into JSON.
 * @param newState
 */
export function saveFilterState(filterKey, newState) {
    try {
        const record = JSON.stringify({data: newState, stationCode: getStationCode()});
        saveIntoLocalStorage(filterKey, record);
    } catch (e) {
    }
}

/**
 * Reads filter state from local storage by translating it into Json.
 * If current station code is different the persisted one, it returns null.
 * @param filterKey
 */
export function readFilterState(filterKey) {
    try {
        const json = getFromLocalStorage(filterKey) || null;
        if (json) {
            const record = JSON.parse(json);
            if (record.stationCode === getStationCode()) {
                return record.data;
            }
        }
    } catch (ignored) {
    }
    return null;
}

/**
 * Checks existence of one-box string in the url
 * @return {boolean}
 */
export function isOneBox() {
    let location = window.location.href
    return location && location.includes("onebox");
}

/**
 * Sorts input list on the basis of sortcolumn and sort direction.
 * @param inputList
 * @param sortColumn
 * @param sortDirection
 * @return {*}
 */
export function sortList(inputList, sortColumn, sortDirection) {
    return inputList.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) {
            return sortDirection === SORT_DIRECTION.ASC ? -1 : 1
        }
        if (a[sortColumn] > b[sortColumn]) {
            return sortDirection === SORT_DIRECTION.ASC ? 1 : -1
        }
        return 0
    })
}

export function getFCLMLink(alias, FCLMEnabled, stationCode) {
        return FCLMEnabled ?
            "https://fclm-portal.amazon.com/employee/ppaTimeDetails?warehouseId=" + stationCode + "&employeeId=" + formatAlias(alias) :
            "https://phonetool.amazon.com/users/" + formatAlias(alias);
    }

/**
 * Trims String to max length for displaying inside tables.
 */
export function trimColValue(value, maxLength){
    if(value && (typeof value === 'string' || value instanceof String) && value.length > maxLength){
        return value.substr(0, maxLength) + "...";
    }
    return value;
}

/**
 * Returns the current time in format [ MonthName dd, YYYY, HH:MM:SS GMT+__:__ ]
 * This pattern may slightly change according to current locale.
 * @param currentDate
 * @param locale
 * @return string
 */
export const getFormattedTime = (currentDate, locale) => {
    return new Intl.DateTimeFormat(locale, DATE_TIME_FORMAT).format(currentDate)
}

export const emptyArrayIfNull = (value) => {
    return Array.isArray(value) ? value : [];
}

/**
 * Return sorted row list with fixed top row identified by a value in specified column
 */
export function sortListWithFixedTopRow(inputList, sortColumn, sortDirection, topRowInfo) {
    return inputList.sort((a, b) => {
        if (!(a[topRowInfo.column] === topRowInfo.value) && b[topRowInfo.column] === topRowInfo.value)
            return 1;
        if (a[topRowInfo.column] === topRowInfo.value && !(b[topRowInfo.column] === topRowInfo.value))
            return -1;

        if (a[sortColumn] < b[sortColumn]) {
            return sortDirection === SORT_DIRECTION.ASC ? -1 : 1
        }
        if (a[sortColumn] > b[sortColumn]) {
            return sortDirection === SORT_DIRECTION.ASC ? 1 : -1
        }
        return 0
    })
}

export function roundTo2Decimals(value){
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Parses domain name and NODE_ENV variable to get current stage.
 * @return {string}
 */
export function getStage() {
    if (isDevelopmentEnv()) {
        return STAGE.DEV;
    }
    const hostname = window.location.hostname;
    if (hostname.includes("integ.")) return STAGE.BETA;
    else if (hostname.includes("master.")) return STAGE.GAMMA;
    else return STAGE.PROD
}

/**
 * Parses domain name and NODE_ENV variable to get current region.
 * @return {string}
 */
export function getRegion() {
    if (isDevelopmentEnv()) {
        return REGION.JP;
    }
    const hostname = window.location.hostname;
    if (hostname.includes("na.")) return REGION.NA;
    else if (hostname.includes("eu.") || hostname.includes("gb.")) return REGION.EU;
    else if (hostname.includes("jp.")) return REGION.JP;
    else if (hostname.includes("in.") || hostname.includes("in-")) return REGION.IN;
    else return REGION.JP;
}

/**
 * Open a page in SCC in new tab
 * It replaces page name in url and opens in new tab
 */
export function openSCCPageInNewTab(prevPageName, newPageName) {
    getDocumentURL().then(documentUrl => {
        const searchURL = new URL(documentUrl.toString().replace(prevPageName, newPageName));
        window.open(searchURL.href, "_blank")
    });
}

export function createRequest(processName, endPoint, requestBody) {
    return {
        processName: processName,
        endPoint: endPoint,
        request: JSON.stringify(requestBody)
    }
}

/**
 * Checks if the RSWS Boson Flow feature is enabled.
 *
 * @returns {Promise<boolean>} A Promise that resolves to a boolean indicating whether the feature is enabled.
 */
export async function isRSWSBosonFlowEnabled() {
    try {
        const response = await getFeaturesFromParent([FEATURES.IS_RSWS_BOSON_FLOW_ENABLED]);
        return response.data && response.data.isRSWSBosonFlowEnabled;
    } catch (ex) {
        console.error("Error occurred while fetching config from Boson App config");
    }
    // Return false if there's an error or if the feature is not enabled.
    return false;
}

export function showRouteStatus(rswsRoute, stationConfig) {
    if (stationConfig.routeDetailsTabViewEnabled) {
        if (rswsRoute.sidelined) {
            return SIDELINED;
        }
        if (rswsRoute.routeCut) {
            return ROUTE_CUT;
        }
        switch (rswsRoute.routeStatus) {
            case STAGED:
            case PARTIALLY_DEPARTED:
                return DEPART_IN_PROGRESS;
            case IN_PROGRESS:
            case ASSIGNABLE:
            case PARTIALLY_STAGED:
                if (rswsRoute.picklists && rswsRoute.picklists.every((picklist) => picklist.status === STAGED)) {
                    return STAGED;
                }
                return PROGRESSING;
            case ASSIGNED:
                return PROGRESSING;
            case NOT_READY:
                return NOT_STARTED;
            default:
                return rswsRoute.routeStatus;
        }
    }
    return rswsRoute.routeStatus;
}




// WEBPACK FOOTER //
// ./src/components/util/Util.js